// Single source of truth helpers for the Verification Configuration editor.
// The shapes here mirror the real rule engine in pickmywork-second-job/kue/emails/verification.js —
// do not invent operators/types that aren't listed there.

export const IDENTIFIER_COLUMN_DELIM = ":,:";

export const OPERATORS = [
  { value: "INCLUDES", label: "Is one of", valueType: "tags" },
  { value: "NOT_INCLUDES", label: "Is not one of", valueType: "tags" },
  { value: "LIKE", label: "Matches pattern (regex)", valueType: "text" },
  { value: "GTE", label: "Greater than or equal to", valueType: "number" },
  { value: "GT", label: "Greater than", valueType: "number" },
  { value: "LTE", label: "Less than or equal to", valueType: "number" },
  { value: "LT", label: "Less than", valueType: "number" },
  { value: "TIME_AFTER", label: "Date is after", valueType: "datetime" },
  { value: "TIME_BEFORE", label: "Date is before", valueType: "datetime" },
  { value: "TIME_BETWEEN", label: "Date is between", valueType: "daterange" },
  { value: "HOURS_BEFORE", label: "Older than (hours)", valueType: "number" },
  { value: "TIME_DIFF_GTE", label: "Time gap ≥", valueType: "timediff" },
  { value: "TIME_DIFF_GT", label: "Time gap >", valueType: "timediff" },
  { value: "TIME_DIFF_LTE", label: "Time gap ≤", valueType: "timediff" },
  { value: "TIME_DIFF_LT", label: "Time gap <", valueType: "timediff" },
  { value: "ARITHMATIC", label: "Combine columns, then compare", valueType: "arithmetic" }
];
export const OPERATOR_MAP = OPERATORS.reduce((m, o) => Object.assign(m, { [o.value]: o }), {});
export const getOperatorMeta = op => OPERATOR_MAP[op] || (op && op.includes("ARITHMATIC") ? OPERATOR_MAP.ARITHMATIC : null);

export const TIME_UNITS = ["minutes", "hours", "days"];
export const ARITHMETIC_OPS = ["ADD", "SUB", "MUL", "DIV"];
export const VALUE_COMPARE_OPS = ["GTE", "GT", "LTE", "LT"];

export const UPDATE_TYPES = [
  { value: 0, label: "Fixed value" },
  { value: 1, label: "Copy from report column(s)" },
  { value: 2, label: "Text template" },
  { value: 3, label: "Text template (mapped values)" }
];

export const UPDATE_DB_OPTIONS = [
  { value: "", label: "Task Status (default)" },
  { value: "form_response_data", label: "A specific form input" },
  { value: "customer_leads", label: "Customer / Lead record" }
];

export const FORM_RESPONSE_FIELDS = ["status", "status2", "status_comment", "remark", "is_tracked"];
export const CUSTOMER_LEAD_FIELDS = ["name", "mobile", "mobile2", "mobile3", "email", "address", "origin_type", "origin_id", "consumer_type", "lead_status"];
export const CUSTOMER_PERSONAL_FIELDS = [
  "year_of_birth", "gender", "earning", "cibil_score", "pan_no", "aadhaar_no", "address", "pincode", "city", "state", "marital_status", "is_bank_account"
];
export const CUSTOMER_BUSINESS_FIELDS = ["business_name", "business_type", "category", "monthly_income", "gst_no", "sales_area_covered"];
export const CUSTOMER_SUB_TABLES = [
  { value: 1, label: "Customer", fields: CUSTOMER_LEAD_FIELDS },
  { value: 2, label: "Personal Details", fields: CUSTOMER_PERSONAL_FIELDS },
  { value: 3, label: "Business Details", fields: CUSTOMER_BUSINESS_FIELDS }
];

export const TRANSFORM_TYPES = [
  { value: "regex", label: "Extract using pattern (regex)" },
  { value: "split", label: "Split text and pick a part" },
  { value: "replace", label: "Find and replace" }
];

let uidCounter = 1;
export const nextUid = () => `rule_${Date.now()}_${uidCounter++}`;

export const splitIdentifierColumns = value => (value ? String(value).split(IDENTIFIER_COLUMN_DELIM).filter(Boolean) : []);
export const joinIdentifierColumns = arr => (arr || []).filter(Boolean).join(IDENTIFIER_COLUMN_DELIM);

const isPlainObject = v => v && typeof v === "object" && !Array.isArray(v);

// Classifies one raw item from `verification_configuration` into a wrapper the UI can render,
// always keeping the original object so unrecognized shapes/keys are never lost.
export const classifyRule = raw => {
  const wrapper = { id: nextUid(), _raw: raw };
  if (!isPlainObject(raw)) return { ...wrapper, _kind: "RAW" };
  if (raw.type === "CAPTURE_MULTIPLE") return { ...wrapper, _kind: "CAPTURE_MULTIPLE", data: { ...raw } };
  if (raw.type === "CAPTURE_DATA") return { ...wrapper, _kind: "CAPTURE_DATA", data: { ...raw } };
  if (isPlainObject(raw.where) && isPlainObject(raw.update)) return { ...wrapper, _kind: "CONDITION", data: JSON.parse(JSON.stringify(raw)) };
  return { ...wrapper, _kind: "RAW" };
};

const CONDITION_KEY_ORDER = ["db", "where", "db_where", "input_where", "update"];

// Rebuilds one array item for saving: known kinds spread their original object first so any
// key the UI doesn't manage survives untouched; RAW items are emitted verbatim (or as edited JSON).
export const serializeRule = wrapper => {
  if (wrapper._kind === "RAW") return wrapper._raw;
  const merged = { ...wrapper._raw, ...wrapper.data };
  if (wrapper._kind !== "CONDITION") return merged;
  // A plain object spread only appends newly-set keys at the end, so a rule created with just
  // where/update and later given a db_where (or input_where) ends up with that field trailing
  // after update — drifting from the rule engine's canonical where/db_where/input_where/update
  // order. Re-order explicitly regardless of which fields the UI happened to fill in first,
  // keeping any unrecognized key (never drop content) appended at the end.
  const ordered = {};
  CONDITION_KEY_ORDER.forEach(key => {
    if (key in merged) ordered[key] = merged[key];
  });
  Object.keys(merged).forEach(key => {
    if (!(key in ordered)) ordered[key] = merged[key];
  });
  return ordered;
};

// Re-runs classification after a raw-JSON edit (e.g. a RAW item was hand-edited into a
// recognizable shape, or a known-kind item was edited via its "Edit as JSON" escape hatch),
// while keeping the same `id` so React keys/reordering stay stable.
export const reclassifyRule = (existingWrapper, parsedValue) => ({ ...classifyRule(parsedValue), id: existingWrapper.id });

export const buildEmptyCondition = () => ({ where: {}, update: {} });
export const buildEmptyCaptureMultiple = () => ({
  type: "CAPTURE_MULTIPLE",
  identifier: { col: "" },
  unique: { col: "", input_id: "" },
  inputs: []
});
export const buildEmptyCaptureData = () => ({ type: "CAPTURE_DATA", codeKey: "", codeMapping: {}, inputs: [] });

export const buildEmptyMapping = () => ({ id: nextUid(), email_id: "", form_ids: [], sheets: [], is_active: "1" });

export const buildEmptyConfig = jobTaskFormId => ({
  config_id: null,
  identifier: 0,
  identifier_column: "",
  identifier_input_id: "",
  rules: [],
  identifierConfiguration: [],
  duplicateConfiguration: [],
  formConfiguration: null,
  mappings: [{ ...buildEmptyMapping(), form_ids: jobTaskFormId ? [String(jobTaskFormId)] : [] }],
  status: 0,
  is_active: "1",
  created_by: null,
  modified_by: null,
  created_on: null,
  modified_on: null
});

// Server already JSON.parse()s the 4 TEXT columns (see pickmywork-backend api/verification_config.js).
export const parseConfigResponse = row => ({
  config_id: row.config_id,
  identifier: row.identifier,
  identifier_column: row.identifier_column || "",
  identifier_input_id: row.identifier_input_id,
  rules: Array.isArray(row.verification_configuration) ? row.verification_configuration.map(classifyRule) : [],
  identifierConfiguration: Array.isArray(row.identifier_configuration) ? row.identifier_configuration : [],
  duplicateConfiguration: Array.isArray(row.duplicate_configuration) ? row.duplicate_configuration : [],
  formConfiguration: row.form_configuration || null,
  mappings: (row.mappings || []).map(m => ({
    id: m.mapping_id || nextUid(),
    mapping_id: m.mapping_id,
    email_id: m.email_id || "",
    form_ids: m.form_ids ? String(m.form_ids).split(",").filter(Boolean) : [],
    sheets: m.sheets ? String(m.sheets).split(",").filter(Boolean) : [],
    is_active: m.is_active
  })),
  status: row.status,
  is_active: row.is_active,
  created_by: row.created_by,
  modified_by: row.modified_by,
  created_on: row.created_on,
  modified_on: row.modified_on
});

export const serializeConfigModel = configModel => ({
  config_id: configModel.config_id || undefined,
  identifier: Number(configModel.identifier),
  identifier_column: configModel.identifier_column,
  identifier_input_id: configModel.identifier_input_id || null,
  verification_configuration: configModel.rules.map(serializeRule),
  identifier_configuration: configModel.identifierConfiguration && configModel.identifierConfiguration.length ? configModel.identifierConfiguration : null,
  duplicate_configuration: configModel.duplicateConfiguration && configModel.duplicateConfiguration.length ? configModel.duplicateConfiguration : null,
  form_configuration: configModel.formConfiguration,
  mappings: configModel.mappings.map(m => ({
    mapping_id: m.mapping_id || undefined,
    email_id: m.email_id,
    form_ids: m.form_ids,
    sheets: m.sheets
  }))
});

// Combined object shown/edited in the JSON View tab — one projection of the same configModel.
export const configModelToJsonView = configModel => ({
  identifier: configModel.identifier,
  identifier_column: splitIdentifierColumns(configModel.identifier_column),
  identifier_input_id: configModel.identifier_input_id,
  verification_configuration: configModel.rules.map(serializeRule),
  identifier_configuration: configModel.identifierConfiguration,
  duplicate_configuration: configModel.duplicateConfiguration,
  form_configuration: configModel.formConfiguration,
  mail_routing: configModel.mappings.map(m => ({ email_id: m.email_id, form_ids: m.form_ids, sheets: m.sheets }))
});

export const jsonViewToConfigModel = (parsed, configModel) => ({
  ...configModel,
  identifier: parsed.identifier,
  identifier_column: joinIdentifierColumns(Array.isArray(parsed.identifier_column) ? parsed.identifier_column : splitIdentifierColumns(parsed.identifier_column)),
  identifier_input_id: parsed.identifier_input_id,
  rules: Array.isArray(parsed.verification_configuration) ? parsed.verification_configuration.map(classifyRule) : [],
  identifierConfiguration: Array.isArray(parsed.identifier_configuration) ? parsed.identifier_configuration : [],
  duplicateConfiguration: Array.isArray(parsed.duplicate_configuration) ? parsed.duplicate_configuration : [],
  formConfiguration: parsed.form_configuration || null,
  mappings: Array.isArray(parsed.mail_routing)
    ? parsed.mail_routing.map((m, i) => ({
        id: (configModel.mappings[i] && configModel.mappings[i].id) || nextUid(),
        mapping_id: configModel.mappings[i] && configModel.mappings[i].mapping_id,
        email_id: m.email_id || "",
        form_ids: m.form_ids || [],
        sheets: m.sheets || [],
        is_active: "1"
      }))
    : configModel.mappings
});

export const validateConfigModel = configModel => {
  const errors = [];
  if (configModel.identifier !== 0 && configModel.identifier !== 1 && configModel.identifier !== "0" && configModel.identifier !== "1")
    errors.push("Choose whether matching is by Reference ID or Mobile Number.");
  if (!configModel.identifier_column) errors.push("At least one identifier report-column name is required.");
  if (!configModel.mappings.length || configModel.mappings.every(m => !m.email_id))
    errors.push("At least one client email is required under Mail Routing.");
  configModel.mappings.forEach(m => {
    if (m.email_id && !m.form_ids.length) errors.push(`Add at least one Form ID for ${m.email_id}.`);
  });
  configModel.rules.forEach((rule, i) => {
    if (rule._kind !== "CONDITION") return;
    Object.keys(rule.data.where || {}).forEach(col => {
      const op = rule.data.where[col] && rule.data.where[col].op;
      if (!op || !getOperatorMeta(op)) errors.push(`Rule ${i + 1}: "${col}" has an unrecognized operator.`);
    });
    Object.keys(rule.data.update || {}).forEach(key => {
      const type = rule.data.update[key].type;
      if (type !== undefined && (type < 0 || type > 3)) errors.push(`Rule ${i + 1}: update "${key}" has an unknown type.`);
      if (type === 1 && !(Array.isArray(rule.data.update[key].val) && rule.data.update[key].val.length))
        errors.push(`Rule ${i + 1}: update "${key}" needs at least one source column to copy from.`);
      if ((type === 2 || type === 3) && !(rule.data.update[key].cols || []).length)
        errors.push(`Rule ${i + 1}: update "${key}" needs at least one template column.`);
    });
  });
  return errors;
};

export const collectKnownColumns = configModel => {
  const cols = new Set();
  splitIdentifierColumns(configModel.identifier_column).forEach(c => cols.add(c));
  configModel.rules.forEach(rule => {
    if (rule._kind !== "CONDITION") return;
    Object.keys(rule.data.where || {}).forEach(c => cols.add(c));
    Object.values(rule.data.update || {}).forEach(u => (u.cols || []).forEach(c => cols.add(c)));
  });
  return Array.from(cols);
};
